import { Link } from "react-router-dom";
import type { Film } from "@/types/film";

interface HotRankingProps {
  films: Film[];
}

function formatViews(views: number) {
  return `${(views / 10000).toFixed(1)} 万`;
}

export default function HotRanking({ films }: HotRankingProps) {
  const max = films.length > 0 ? films[0].views : 1;

  return (
    <section className="w-full bg-primary-900 px-4 py-12 md:px-10 md:py-16">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-400">Trending</span>
          <h2 className="mt-2 font-heading text-2xl font-semibold text-background-50 md:text-3xl">
            热门伦理影视榜
          </h2>
          <p className="mt-2 text-sm text-background-300">
            按站内浏览量排序，每周更新。榜单只反映阅读热度，不代表作品评价。
          </p>

          <ul className="mt-7 flex flex-col divide-y divide-background-50/10">
            {films.map((film, index) => (
              <li key={film.id}>
                <Link to={`/detail/${film.id}`} className="group flex items-center gap-4 py-3.5 cursor-pointer">
                  <span
                    className={`w-6 shrink-0 font-heading text-lg font-semibold ${
                      index < 3 ? "text-accent-400" : "text-background-400"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="h-14 w-11 shrink-0 overflow-hidden rounded-sm bg-primary-950">
                    <img
                      src={film.cover}
                      alt={`${film.title}封面`}
                      title={`${film.title} - 伦理视频热门榜`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-heading text-sm font-medium text-background-50 transition-colors group-hover:text-accent-300 md:text-base">
                        {film.title}
                      </h3>
                      <span className="shrink-0 text-[11px] text-background-400">{film.year}</span>
                    </div>
                    <p className="mt-1 truncate text-xs text-background-400">{film.genres.join(" / ")}</p>
                    <div className="mt-2 h-[3px] w-full max-w-[260px] rounded-full bg-background-50/10">
                      <div
                        className="h-full rounded-full bg-accent-500/80"
                        style={{ width: `${Math.max((film.views / max) * 100, 8)}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    <span className="flex items-center gap-0.5 text-sm font-medium text-accent-300">
                      <i className="ri-star-fill text-[11px]" />
                      {film.rating.toFixed(1)}
                    </span>
                    <span className="text-[11px] text-background-400">{formatViews(film.views)}浏览</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="flex h-full flex-col justify-between rounded-lg bg-primary-950/60 p-6 md:p-7">
            <div>
              <i className="ri-double-quotes-l text-3xl text-accent-500" />
              <p className="mt-4 font-heading text-lg leading-relaxed text-background-100 md:text-xl">
                伦理题材真正的张力，不在于谁对谁错，而在于每个人都有一套说得通的理由。
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-background-400">伦理视频 · 编辑部</p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-background-50/10 pt-6">
              <div>
                <p className="font-heading text-2xl font-semibold text-background-50">11</p>
                <p className="mt-1 text-xs text-background-400">收录作品</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-semibold text-background-50">7</p>
                <p className="mt-1 text-xs text-background-400">深度影评</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-semibold text-background-50">5</p>
                <p className="mt-1 text-xs text-background-400">覆盖地区</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-semibold text-background-50">3</p>
                <p className="mt-1 text-xs text-background-400">分析维度</p>
              </div>
            </div>
            <Link
              to="/reviews"
              className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-50 transition-colors hover:border-accent-400 hover:bg-background-50/10 cursor-pointer"
            >
              进入影评专题
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}